/**
 * DateTimeService — the single source of truth for rendering timestamps.
 *
 * Architecture:
 *   • The backend stores every timestamp as an ISO 8601 UTC string.
 *   • The DATABASE and API always stay UTC. Conversion happens ONLY here, in
 *     the presentation layer.
 *   • The display timezone is the logged-in user's preference (default
 *     "Asia/Tokyo" / JST). AuthContext calls setUserTimezone() after loading
 *     the user, so every consumer of these helpers converts automatically.
 *
 * Never format dates by hand (no `new Date().toLocaleString()`, no slicing an
 * ISO string) — always go through this service so every screen is consistent,
 * DST-safe and timezone-configurable.
 */

const DEFAULT_TZ = "Asia/Tokyo";

// Module-level display preferences, updated at login by AuthContext.
let _tz = DEFAULT_TZ;
let _locale = "ja"; // "ja" → YYYY/MM/DD, others → YYYY-MM-DD

// Common IANA zone → friendly abbreviation (used only in exports + footer).
const TZ_ABBREV = {
  "Asia/Tokyo": "JST",
  "Asia/Jakarta": "WIB",
  "Asia/Manila": "PHT",
  "UTC": "UTC",
  "America/New_York": "ET",
  "Europe/London": "UK",
};

// Options for the Settings → Preferences timezone dropdown.
export const TZ_OPTIONS = [
  { value: "Asia/Tokyo", labelJa: "東京 (JST)", labelEn: "Tokyo (JST)" },
  { value: "Asia/Jakarta", labelJa: "ジャカルタ (WIB)", labelEn: "Jakarta (WIB)" },
  { value: "Asia/Manila", labelJa: "マニラ (PHT)", labelEn: "Manila (PHT)" },
  { value: "UTC", labelJa: "UTC", labelEn: "UTC" },
  { value: "America/New_York", labelJa: "ニューヨーク (EST/EDT)", labelEn: "New York (EST/EDT)" },
  { value: "Europe/London", labelJa: "ロンドン (GMT/BST)", labelEn: "London (GMT/BST)" },
];

function isValidTz(tz) {
  if (!tz || typeof tz !== "string") return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch (_e) {
    return false;
  }
}

/** Set the display timezone (from user profile). Invalid → fallback JST. */
export function setUserTimezone(tz) {
  _tz = isValidTz(tz) ? tz : DEFAULT_TZ;
}

/** Set the display locale for separators (from UI language). */
export function setUserLocale(loc) {
  _locale = loc === "en" || (typeof loc === "string" && loc.startsWith("en")) ? "en" : "ja";
}

export function getUserTimezone() { return _tz; }

/** Friendly timezone label ("JST"). Falls back to the GMT offset name. */
export function tzLabel(tz = _tz) {
  if (TZ_ABBREV[tz]) return TZ_ABBREV[tz];
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, timeZoneName: "short",
    }).formatToParts(new Date());
    return (parts.find((p) => p.type === "timeZoneName") || {}).value || tz;
  } catch (_e) {
    return tz;
  }
}

function toDate(v) {
  if (v == null || v === "") return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  let s = String(v);
  // A naive ISO datetime (no tz) is stored as UTC by the backend → append Z.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) {
    s = s + "Z";
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function parts(v, tz = _tz) {
  const d = toDate(v);
  if (!d) return null;
  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone: isValidTz(tz) ? tz : DEFAULT_TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  });
  const p = Object.fromEntries(dtf.formatToParts(d).map((x) => [x.type, x.value]));
  return {
    year: p.year, month: p.month, day: p.day,
    hour: p.hour === "24" ? "00" : p.hour, minute: p.minute, second: p.second,
  };
}

function dateSep() { return _locale === "en" ? "-" : "/"; }

// ── Canonical formatters (use these) ────────────────────────────────────────
/** "YYYY/MM/DD" (ja) or "YYYY-MM-DD" (en). Null-safe → "".
 *  A pure calendar date ("YYYY-MM-DD", no time) is treated as timezone-agnostic
 *  and never shifted — otherwise a due-date could jump a day in western zones. */
export function formatDate(v) {
  if (v == null || v === "") return "";
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const s = dateSep();
    return `${m[1]}${s}${m[2]}${s}${m[3]}`;
  }
  const p = parts(v);
  if (!p) return "";
  const s = dateSep();
  return `${p.year}${s}${p.month}${s}${p.day}`;
}

/** "YYYY/MM/DD HH:mm" (ja) or "YYYY-MM-DD HH:mm" (en). */
export function formatDateTime(v) {
  const p = parts(v);
  if (!p) return "";
  const s = dateSep();
  return `${p.year}${s}${p.month}${s}${p.day} ${p.hour}:${p.minute}`;
}

/** With seconds — audit logs / precise timestamps. */
export function formatDateTimeFull(v) {
  const p = parts(v);
  if (!p) return "";
  const s = dateSep();
  return `${p.year}${s}${p.month}${s}${p.day} ${p.hour}:${p.minute}:${p.second}`;
}

/** "HH:mm" — chat / short chips. */
export function formatTime(v) {
  const p = parts(v);
  return p ? `${p.hour}:${p.minute}` : "";
}

/** "M/D HH:mm" — compact. */
export function formatShort(v) {
  const p = parts(v);
  return p ? `${Number(p.month)}/${Number(p.day)} ${p.hour}:${p.minute}` : "";
}

/** Date/time WITH the timezone label — for the footer/build stamp + exports. */
export function formatStamp(v) {
  const s = formatDateTime(v);
  return s ? `${s} ${tzLabel()}` : "";
}

/** Relative time ("たった今 / 5分前 / 3時間前 / 2日前"); older → absolute. */
export function formatRelative(v) {
  const d = toDate(v);
  if (!d) return "";
  const diff = Date.now() - d.getTime();
  const sec = Math.round(diff / 1000);
  const en = _locale === "en";
  if (sec < 0) return formatDateTime(v);
  if (sec < 45) return en ? "just now" : "たった今";
  const min = Math.round(sec / 60);
  if (min < 60) return en ? `${min}m ago` : `${min}分前`;
  const hr = Math.round(min / 60);
  if (hr < 24) return en ? `${hr}h ago` : `${hr}時間前`;
  const day = Math.round(hr / 24);
  if (day <= 7) return en ? `${day}d ago` : `${day}日前`;
  return formatDateTime(v);
}

/** Localised long form, pinned to the user timezone. */
export function formatLocale(v, opts = {}) {
  const d = toDate(v);
  if (!d) return "";
  return d.toLocaleString(_locale === "en" ? "en-US" : "ja-JP",
    { timeZone: isValidTz(_tz) ? _tz : DEFAULT_TZ, ...opts });
}

// ── Backward-compatible aliases (existing 18 importers keep working, now
//    timezone- and locale-aware automatically) ────────────────────────────────
export const formatJST = formatDateTime;
export const formatJSTFull = formatDateTimeFull;
export const formatJSTDate = formatDate;
export const formatJSTTime = formatTime;
export const formatJSTShort = formatShort;
export const formatJSTLocale = formatLocale;
